// @ts-strict-ignore
import {Section, SectionItem, TooltipInfo} from 'common/components/items-menu/types';
import rollingLineSvg from 'common/assets/images/charts/rolling-line.svg';
import rollingStackedLineSvg from 'common/assets/images/charts/rolling-stacked-line.svg';
import cumulativeLineSvg from 'common/assets/images/charts/cumulative-line.svg';
import cumulativeStackedLineSvg from 'common/assets/images/charts/cumulative-stacked-line.svg';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {
  AdvancedMenuValue,
  Calculation,
  CalculationTypes,
  CountBy,
  CountType,
  Entry,
  InsightsCountType,
  isMetricEntry,
  RetentionCountType,
} from 'common/query-builder/query-entry-section/types';
import {getSelectedGroupLabel} from 'common/query-builder/query-entry-section/util';
import {AggregationGroupLabel, QueryOrigin} from 'common/query-builder/types/shared-types';
import {ALL_MATH_TYPES, CountUsersOnceState} from 'common/report/constants';
import {
  Behavior,
  BehaviorMeasurement,
  ChartPlotStyle,
  COUNT_USERS_ONCE_TYPE,
  FUNNELS_MATH_TYPES,
  MATH_TYPE,
  MetricType,
  PER_USER_AGGREGATIONS,
} from 'common/types/reports/bookmark';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {getIconForPropertyType, renameSCDProp} from 'common/report/util/properties';
import {toTitleCase} from 'common/util/string';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {RollingWindowSize} from 'common/util';
import {ROLLING_WINDOW_SIZE_OPTIONS} from 'common/report/insights/models/constants';
import {Segmentation} from 'common/types/reports/query';
import {FunnelsCountType} from 'common/report/funnels/models/constants';
import {MultiAttribution} from 'common/widgets/multi-attribution-menu/types';
import {Translatable} from 'common/i18n';
import {SvgIconType} from 'common/components/svg-icon/types';
import {BehaviorMetric} from 'app_api/projects/metrics/types';
import {convertBookmarkPropertyToPropertySelection} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/metric-entry-util';

import {isProperty, isScdProperty} from '../properties-menu/types';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {getSelectedPropertyName} from '../utils/property-util';
import {RECURRING_REVENUE_CHANGE} from '../properties-menu/constants';
import {
  AggregationSelector,
  COUNT_USERS_ONCE_OPTIONS,
  CountLabelGenerator,
  CountLabelGeneratorMappings,
  CountSelections,
  CountTypeSelector,
  DefaultCountUsersOnceType,
  DisabledCountTypes,
  FUNNELS_COUNT_MENU_ITEMS,
  FunnelsCountSubMenuHeader,
  getOperatorLabel,
  INSIGHTS_COUNT_MENU_ITEMS,
  InsightsCountSubMenuHeader,
  RequiredSelectionsParams,
  RETENTION_COUNT_MENU_ITEMS,
  SelectorLabel,
  SubSelectionTypes,
} from './constants';

const DEFAULT_ICON = `type-number`;

/**
 * Recursively prunes a count-menu aggregation subtree to only the options whose
 * committed operator is permitted. An option is kept when its own operator is
 * allowed, or when it still has any allowed descendant after filtering.
 */
function filterAggregationsByAllowed(
  aggregations: AggregationSelector[],
  allowed: Set<MATH_TYPE>,
): AggregationSelector[] {
  return aggregations
    .map((aggregation) => ({
      ...aggregation,
      ...(aggregation.subSections ? {subSections: filterAggregationsByAllowed(aggregation.subSections, allowed)} : {}),
      ...(aggregation.subAggregations
        ? {subAggregations: filterAggregationsByAllowed(aggregation.subAggregations, allowed)}
        : {}),
    }))
    .filter(
      (aggregation) =>
        (!!aggregation.operator && allowed.has(aggregation.operator)) ||
        (aggregation.subSections?.length ?? 0) > 0 ||
        (aggregation.subAggregations?.length ?? 0) > 0,
    );
}

/**
 * Filters the count-type menu to only the measurements a restricted context
 * permits. Behavioral properties, for example, cannot use across-users
 * When `allowedAggregators` is undefined or empty the menu is returned unchanged.
 */
export function filterCountTypesByAllowedAggregators(
  countTypes: CountTypeSelector[],
  allowedAggregators?: MATH_TYPE[],
): CountTypeSelector[] {
  if (!allowedAggregators?.length) {
    return countTypes;
  }
  const allowed = new Set(allowedAggregators);
  return countTypes
    .map((countType) => ({
      ...countType,
      ...(countType.aggregations ? {aggregations: filterAggregationsByAllowed(countType.aggregations, allowed)} : {}),
    }))
    .filter(
      (countType) =>
        (!!countType.operator && allowed.has(countType.operator)) || (countType.aggregations?.length ?? 0) > 0,
    );
}

export function isAdvancedValueCalculation(value: AdvancedMenuValue): value is Calculation {
  return Object.prototype.hasOwnProperty.call(value, `calculationType`);
}
export function getPropertyName(
  property: PropertySectionItem,
  {
    includeListItemGroup = false,
    showSpecialDatePropertyUnit = false,
  }: {
    includeListItemGroup?: boolean;
    showSpecialDatePropertyUnit?: boolean;
  } = {},
): Translatable {
  if (!property) return ``;
  if (!isProperty(property)) {
    return property.label;
  }
  return getSelectedPropertyName({
    selected: property,
    showSpecialDatePropertyUnit,
    expandListProperties: includeListItemGroup,
  });
}

export function getPropertyIcon(property: PropertySectionItem): SvgIconType {
  if (isProperty(property)) {
    if (property.icon) {
      return property.icon as SvgIconType;
    }
    if (property.value === RECURRING_REVENUE_CHANGE.value) {
      return `computed`;
    }
    const type = property?.joinPropertyType ?? property?.type ?? null;
    if (type) {
      return getIconForPropertyType(type, {isCustom: !!property.customProperty, isScd: isScdProperty(property)});
    }
    return DEFAULT_ICON;
  }
  return DEFAULT_ICON;
}

export function getNestedPropertyIcon(nestedProperty: Segmentation) {
  const type = nestedProperty?.propertyType;
  if (type) {
    return getIconForPropertyType(type, {isCustom: false});
  }
  return DEFAULT_ICON;
}

export function getAggregationIcon(
  countTypeSelection: SectionItem<CountTypeSelector>,
  selectionType: SubSelectionTypes,
): string {
  const uniqueCountTypes = [InsightsCountType.UniqueUsers, FunnelsCountType.Unique, RetentionCountType.Unique];
  const {countType} = countTypeSelection.value;
  if (uniqueCountTypes.includes(countType) || selectionType === SubSelectionTypes.AggregationPerUser) {
    return `team`;
  }
  return `aggregate`;
}

function formatSelectorLabel(
  label: SelectorLabel,
  {
    dataGroupId,
    dataGroups,
    metricType,
    queryOrigin,
  }: {
    dataGroupId: string;
    dataGroups: DataGroup[];
    metricType?: MetricType;
    queryOrigin?: QueryOrigin;
  },
) {
  return label({
    [AggregationGroupLabel.Group]: dataGroupId
      ? getSelectedGroupLabel({dataGroupId, dataGroups})
      : ({t}) => t(`query-builder.dg-user`).toLowerCase(),
    [AggregationGroupLabel.GroupTitle]: ({print}) =>
      toTitleCase(print(getSelectedGroupLabel({dataGroupId, dataGroups}))),
    [AggregationGroupLabel.PluralGroup]: dataGroupId
      ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
      : ({t}) => t(`query-builder.dg-user`, {count: 2}).toLowerCase(),
    [AggregationGroupLabel.PluralGroupTitle]: ({print}) =>
      toTitleCase(print(getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}))),
    [AggregationGroupLabel.MetricType]: metricType,
    [AggregationGroupLabel.QueryOrigin]: queryOrigin,
  });
}

function formatOutputLabel({
  label,
  aggregationOperator,
  aggregationOperatorPerUser,
  countUsersOnceType,
  property,
  dataGroupId,
  dataGroups,
  metricType,
  queryOrigin,
  withDataGroupSelect = false,
  percentile,
}: {
  label: CountLabelGenerator;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: PER_USER_AGGREGATIONS;
  countUsersOnceType: COUNT_USERS_ONCE_TYPE;
  property: PropertySectionItem;
  dataGroupId: string;
  dataGroups: DataGroup[];
  metricType: MetricType;
  queryOrigin?: QueryOrigin;
  /** When true, uses generic labels without data group information (for metric-level data group support) */
  withDataGroupSelect?: boolean;
  percentile?: number;
}): Translatable {
  return label({
    [CountSelections.AggregationOperator]: aggregationOperator,
    [CountSelections.AggregationOperatorPerUser]: aggregationOperatorPerUser,
    [CountSelections.Percentile]: percentile,
    [CountSelections.CountUsersOnceType]: countUsersOnceType,
    [CountSelections.PropertyName]: getPropertyName(property, {
      includeListItemGroup: true,
      showSpecialDatePropertyUnit: true,
    }),
    [CountSelections.PropertyValue]: property?.value,
    [AggregationGroupLabel.Group]: dataGroupId
      ? getSelectedGroupLabel({dataGroupId, dataGroups})
      : ({t}) => t(`query-builder.dg-user`).toLowerCase(),
    [AggregationGroupLabel.GroupTitle]: ({print}) =>
      toTitleCase(print(getSelectedGroupLabel({dataGroupId, dataGroups}))),
    [AggregationGroupLabel.PluralGroup]: dataGroupId
      ? getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true})
      : ({t}) => t(`query-builder.dg-user`, {count: 2}).toLowerCase(),
    [AggregationGroupLabel.PluralGroupTitle]: ({print}) =>
      toTitleCase(print(getSelectedGroupLabel({dataGroupId, dataGroups, isPlural: true}))),
    [AggregationGroupLabel.MetricType]: metricType,
    [AggregationGroupLabel.QueryOrigin]: queryOrigin,
    withDataGroupSelect,
  });
}

export function getSelectedAggregationLabel({
  metricType,
  operator,
  countTypeSelection,
  percentile,
}: {
  metricType: MetricType;
  operator: MATH_TYPE;
  countTypeSelection: SectionItem<CountTypeSelector>;
  percentile?: number;
}): Translatable {
  const label = getOperatorLabel(operator, percentile);
  if (label) {
    return label;
  }

  if (metricType === MetricType.Event && countTypeSelection.value.operator === ALL_MATH_TYPES.UNIQUE) {
    return ({t}) => t(`query-builder.count-menu.select-unique`);
  }

  if (metricType === MetricType.Funnel && countTypeSelection.value.countType === FunnelsCountType.ConversionRate) {
    return ({t}) => t(`query-builder.count-menu.select-count`);
  }

  return ({t}) => t(`query-builder.count-menu.select-agg`);
}

export function getSelectedCountByLabel({
  selected,
  metricType,
  dataGroupId,
  dataGroups,
  withDataGroupSelect = false,
}: {
  selected: CountBy;
  metricType: MetricType;
  dataGroupId: string;
  dataGroups: DataGroup[];
  /** When true, uses generic labels without data group information (for metric-level data group support) */
  withDataGroupSelect?: boolean;
}): Translatable {
  const {aggregationOperator, aggregationOperatorPerUser, countType, countUsersOnceType, property, percentile} =
    selected;
  const label = metricType && CountLabelGeneratorMappings.get(metricType).get(countType);
  let selectedLabel = ``;
  if (
    property?.value === RECURRING_REVENUE_CHANGE.value &&
    metricType === MetricType.People &&
    isProperty(property) &&
    property.behavior?.property?.value
  ) {
    selectedLabel = ` on ${renameSCDProp(property.behavior?.property?.value)}`;
  }

  if (!label) {
    return ``;
  }

  return ({print}) =>
    print(
      formatOutputLabel({
        label,
        aggregationOperator,
        aggregationOperatorPerUser,
        percentile,
        countUsersOnceType,
        property,
        dataGroupId,
        dataGroups,
        metricType,
        withDataGroupSelect,
      }),
    ) + selectedLabel;
}

export function getSubSelectionsForCountType({
  countTypeSelection,
  property,
}: {
  // countTypeSelection coming from constant#INSIGHTS_COUNT_MENU_ITEMS
  countTypeSelection: SectionItem<CountTypeSelector>;
  // property is the selected property in aggregation (if presented)
  property: PropertySectionItem;
}): SubSelectionTypes[] {
  // the requiredSubSelections could be either
  // 1. function takes in {property} as only arg
  // 2. a static array
  // we want to get an array like [`aggregation`, `aggregationPerUser`, `property`, `nestedProperty`]
  // to tell the count menu what sub sections to render.
  // The property is needed here because we conditionally show `nestedProperty` if it is a list of object
  const requiredSubSelections =
    typeof countTypeSelection.value.requiredSubSelections === `function`
      ? countTypeSelection.value.requiredSubSelections({property})
      : countTypeSelection.value.requiredSubSelections || [];
  return [...requiredSubSelections];
}

export function shouldCommitSelection({
  countTypeSelection,
  property = null,
  aggregationOperatorPerUser = null,
  aggregationOperator = null,
  countUsersOnceType = null,
}: {
  countTypeSelection: SectionItem<CountTypeSelector>;
  property?: PropertySectionItem;
  aggregationOperatorPerUser?: PER_USER_AGGREGATIONS;
  aggregationOperator?: MATH_TYPE;
  countUsersOnceType?: COUNT_USERS_ONCE_TYPE;
}): boolean {
  const hasRequiredSelections = countTypeSelection.value.hasRequiredSelections;
  const replacements: RequiredSelectionsParams = {
    [SubSelectionTypes.Property]: property,
    [SubSelectionTypes.NestedProperty]: isProperty(property) ? property.listItemGroup : null,
    [SubSelectionTypes.AggregationPerUser]: aggregationOperatorPerUser,
    [SubSelectionTypes.Aggregation]: aggregationOperator,
    [SubSelectionTypes.CountUsersOnce]: countUsersOnceType,
  };

  return hasRequiredSelections(replacements);
}

export function generateSectionItem({
  item,
  dataGroupId,
  dataGroups,
  queryOrigin,
}: {
  item: CountTypeSelector | AggregationSelector;
  dataGroupId: string;
  dataGroups: DataGroup[];
  queryOrigin?: QueryOrigin;
}): SectionItem {
  return {
    label: formatSelectorLabel(item.label, {dataGroupId, dataGroups}),
    shortLabel: item.shortLabel ? formatSelectorLabel(item.shortLabel, {dataGroupId, dataGroups}) : undefined,
    value: item,
    tooltip: item.description
      ? {
          text: formatSelectorLabel(item.description, {dataGroupId, dataGroups, queryOrigin}),
          size: DisplaySize.Small,
          placement: Placement.Right,
        }
      : null,
    isDisabled: queryOrigin ? DisabledCountTypes[queryOrigin].includes(item.operator) : false,
  };
}

function getSubSelectionHeaderInfo({
  metricType,
  countType,
  subSelectionType,
  dataGroupId,
  dataGroups,
}: {
  metricType: MetricType;
  countType: CountType;
  subSelectionType: SubSelectionTypes;
  dataGroupId: string;
  dataGroups: DataGroup[];
}) {
  let subHeader;
  if (metricType === MetricType.Event) {
    subHeader = InsightsCountSubMenuHeader?.[countType]?.[subSelectionType];
  } else if (metricType === MetricType.Funnel) {
    subHeader = FunnelsCountSubMenuHeader?.[countType]?.[subSelectionType];
  }

  if (subHeader) {
    const {label, description, icon} = subHeader;
    return {
      label: formatSelectorLabel(label, {dataGroupId, dataGroups}),
      tooltip: {
        text: formatSelectorLabel(description, {dataGroupId, dataGroups}),
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
      },
      icon,
    };
  }
  return null;
}

function createAggregationsSection({
  aggregations,
  label,
  icon,
  tooltip,
  dataGroupId,
  dataGroups,
  queryOrigin,
}: {
  aggregations: AggregationSelector[];
  label?: string;
  icon?: string;
  tooltip?: TooltipInfo;
  dataGroupId?: string;
  dataGroups?: DataGroup[];
  queryOrigin?: QueryOrigin;
}): Array<Section<AggregationSelector>> {
  const aggregationItems: Array<SectionItem<AggregationSelector>> = aggregations.map((aggregation) => {
    return {
      ...generateSectionItem({item: aggregation, dataGroupId, dataGroups, queryOrigin}),
      subSections: aggregation.subSections
        ? createAggregationsSection({
            aggregations: aggregation.subSections,
            dataGroupId,
            dataGroups,
            queryOrigin,
          })
        : null,
    };
  });
  return aggregationItems.length ? [{items: aggregationItems, label, icon, tooltip}] : [];
}

export function getAggregationsSectionForCountType({
  countTypeSelections,
  countTypeIndex,
  subSelectionType,
  aggregationOperator,
  property,
  metricType,
  dataGroupId,
  dataGroups,
  queryOrigin,
}: {
  countTypeSelections: Array<SectionItem<CountTypeSelector>>;
  countTypeIndex: number;
  property: PropertySectionItem;
  subSelectionType: SubSelectionTypes;
  aggregationOperator?: MATH_TYPE;
  metricType?: MetricType;
  dataGroupId: string;
  dataGroups: DataGroup[];
  queryOrigin?: QueryOrigin;
}): Array<Section<AggregationSelector>> {
  const countTypeSelection = countTypeSelections[countTypeIndex];
  const {countType, aggregations, requiredSubSelections: requiredSubSelectionsOrFn} = countTypeSelection.value;
  const headerInfo = getSubSelectionHeaderInfo({metricType, countType, subSelectionType, dataGroupId, dataGroups});

  let label, tooltip, icon;
  if (headerInfo) {
    label = headerInfo.label;
    tooltip = headerInfo.tooltip;
    icon = headerInfo.icon;
  }
  if (!aggregations) return [];
  const requiredSubSelections =
    typeof requiredSubSelectionsOrFn === `function` ? requiredSubSelectionsOrFn({property}) : requiredSubSelectionsOrFn;
  if (requiredSubSelections.includes(SubSelectionTypes.AggregationPerUser) && aggregationOperator) {
    const subAggregations =
      aggregations.find((aggregation) => aggregation.operator === aggregationOperator)?.subAggregations ||
      aggregations
        .find((aggregation) => !!aggregation.subSections)
        ?.subSections?.find((aggregation) => aggregation.operator === aggregationOperator)?.subAggregations;
    return createAggregationsSection({
      aggregations: subAggregations,
      label,
      icon,
      tooltip,
      dataGroupId,
      dataGroups,
      queryOrigin,
    });
  }

  return createAggregationsSection({
    aggregations,
    label,
    icon,
    tooltip,
    dataGroupId,
    dataGroups,
    queryOrigin,
  });
}

function findMatchingAggregation(
  aggregation: AggregationSelector,
  callback: (params: {operator: MATH_TYPE; perUserOperator: PER_USER_AGGREGATIONS}) => boolean,
): AggregationSelector {
  if (callback({operator: aggregation.operator, perUserOperator: aggregation.perUserOperator})) {
    return aggregation;
  }
  const aggregations = aggregation.subAggregations || aggregation.subSections || [];
  for (const aggregationItem of aggregations) {
    const matchedItem = findMatchingAggregation(aggregationItem, callback);
    if (matchedItem) return matchedItem;
  }
  return null;
}

/** A tooltip text to be displayed in Aggregation menu header */
export function getSubSelectionDescription({
  searchFirstLevelAggregation,
  countTypeSelection,
  aggregationOperator,
  aggregationOperatorPerUser,
  dataGroupId,
  dataGroups,
}: {
  searchFirstLevelAggregation: boolean;
  countTypeSelection: SectionItem<CountTypeSelector>;
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: PER_USER_AGGREGATIONS;
  dataGroupId: string;
  dataGroups: DataGroup[];
}): Translatable {
  const aggregations = countTypeSelection.value.aggregations;

  let searchCriteria;
  if (searchFirstLevelAggregation) {
    const hasAggregator = Boolean(aggregationOperator);
    searchCriteria = {
      operator: aggregationOperator ?? null,
      perUserOperator: hasAggregator ? null : aggregationOperatorPerUser,
    };
  } else {
    searchCriteria = {
      operator: aggregationOperator ?? null,
      perUserOperator: aggregationOperatorPerUser ?? null,
    };
  }
  const condition = ({operator, perUserOperator}) => {
    return (
      (operator ?? null) === searchCriteria.operator && (perUserOperator ?? null) === searchCriteria.perUserOperator
    );
  };
  for (const aggregation of aggregations) {
    const selectedAggregation = findMatchingAggregation(aggregation, condition);
    const description = selectedAggregation?.description;
    if (description) {
      return formatSelectorLabel(description, {dataGroupId, dataGroups});
    }
  }

  return ``;
}

export function generateCountCalculationMenuOptions({
  selectedPlotStyle,
  selectedCalculation,
  selectedDateUnit,
  includeStandardItem,
}: {
  selectedPlotStyle: ChartPlotStyle;
  selectedCalculation: Calculation;
  selectedDateUnit: RollingWindowSize;
  /** used for displaying an item that will reset selection */
  includeStandardItem?: boolean;
}): Array<Section<Calculation | null>> {
  const {calculationType, rollingWindowSize} = selectedCalculation;

  const items = [
    {
      label: ({t}) => t(`query-builder.count-menu.calculations.rolling-avg`),
      isSelected: calculationType === CalculationTypes.Rolling,
      subSections: [
        {
          items: ROLLING_WINDOW_SIZE_OPTIONS[selectedDateUnit].map((size) => ({
            label: ({t}) =>
              t(`reports.time-unit.x-units`, {
                count: size,
                timeUnit: t(`reports.time-unit.${selectedDateUnit}`, {count: size}).toLowerCase(),
              }),
            value: {
              calculationType: CalculationTypes.Rolling,
              rollingWindowSize: size,
            },
            tooltip: {
              size: DisplaySize.Small,
              placement: Placement.Right,
              'image-src': selectedPlotStyle === ChartPlotStyle.Stacked ? rollingStackedLineSvg : rollingLineSvg,
              text: ({t}) =>
                t(`query-builder.count-menu.calculations.rolling-avg-tooltip`, {
                  windowSize: size,
                  windowUnit: t(`reports.time-unit.${selectedDateUnit}`, {count: size}).toLowerCase(),
                }),
            },
            isSelected: size === rollingWindowSize,
          })),
        },
      ],
    },
    {
      label: ({t}) => t(`query-builder.count-menu.calculations.cumulative-sum`),
      isSelected: calculationType === CalculationTypes.Cumulative,
      value: {
        calculationType: CalculationTypes.Cumulative,
      },
      tooltip: {
        size: DisplaySize.Small,
        placement: Placement.Right,
        'image-src': selectedPlotStyle === ChartPlotStyle.Stacked ? cumulativeStackedLineSvg : cumulativeLineSvg,
        text: ({t}) => t(`query-builder.count-menu.calculations.cumulative-sum-tooltip`),
      },
    },
  ];

  if (includeStandardItem) {
    items.unshift({
      label: ({t}) => t(`common.standard`),
      value: null,
      isSelected: selectedCalculation === null || calculationType === null,
      tooltip: null,
    });
  }

  return [
    {
      label: ({t}) => t(`query-builder.count-menu.calculations.title`),
      icon: `info-xs`,
      tooltip: {
        size: DisplaySize.Small,
        placement: Placement.RightCenter,
        text: ({t}) =>
          `${t(`query-builder.count-menu.calculations.tooltip`)}\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/insights#analysis-settings)`,
      },
      items,
    },
  ];
}

export function generateCountUsersOnceMenuOptions({
  selectedCountUsersOnceType,
  timePeriodLabel,
  countUsersOnceState,
  metricType,
  dataGroupId,
}: {
  selectedCountUsersOnceType: COUNT_USERS_ONCE_TYPE;
  timePeriodLabel: string;
  countUsersOnceState: CountUsersOnceState;
  metricType: MetricType;
  dataGroupId: string;
}): Section[] {
  const items = Object.values(COUNT_USERS_ONCE_TYPE)
    .filter((type) => {
      if ([MetricType.Funnel, MetricType.Retention, MetricType.RetentionFrequency].includes(metricType)) {
        // segmentMethod 'last' is currently unsupported for these metrics/reports
        return type !== COUNT_USERS_ONCE_TYPE.LAST;
      }
      return true;
    })
    .map((type) => ({
      label: COUNT_USERS_ONCE_OPTIONS[type].label({defaultType: DefaultCountUsersOnceType.get(metricType)}),
      value: {
        countUsersOnceType: type,
      },
      tooltip: {
        size: DisplaySize.Small,
        placement: Placement.Right,
        text:
          countUsersOnceState === CountUsersOnceState.Required && type === COUNT_USERS_ONCE_TYPE.MULTIPLE
            ? ({t}) => t(`query-builder.count-menu.count-users-once.no-every-attribution`)
            : COUNT_USERS_ONCE_OPTIONS[type].description({timePeriod: timePeriodLabel}),
      },
      isDisabled: !COUNT_USERS_ONCE_OPTIONS[type].enabledStates.includes(countUsersOnceState),
      isSelected:
        countUsersOnceState === CountUsersOnceState.Required && type === COUNT_USERS_ONCE_TYPE.MULTIPLE
          ? false
          : type === selectedCountUsersOnceType,
    }));

  return [
    {
      label: ({t}) =>
        dataGroupId
          ? t(`query-builder.count-menu.count-users-once.groups-title`)
          : t(`query-builder.count-menu.count-users-once.users-title`),
      items,
    },
  ];
}

export function getCountUsersOnceTypeFromEntrySelection({
  aggregationOperator,
  selectedCountUsersOnceType,
  metricType,
  multiAttribution,
}: {
  aggregationOperator: MATH_TYPE | undefined;
  selectedCountUsersOnceType: COUNT_USERS_ONCE_TYPE | undefined;
  metricType: MetricType;
  multiAttribution: MultiAttribution | null | undefined;
}): COUNT_USERS_ONCE_TYPE | null {
  let nextCountUsersOnceType = selectedCountUsersOnceType || DefaultCountUsersOnceType.get(metricType);

  if (multiAttribution && nextCountUsersOnceType === COUNT_USERS_ONCE_TYPE.MULTIPLE) {
    const metricsWithoutCountLastSupport = [MetricType.Funnel, MetricType.Retention, MetricType.RetentionFrequency];
    nextCountUsersOnceType = metricsWithoutCountLastSupport.includes(metricType)
      ? COUNT_USERS_ONCE_TYPE.FIRST
      : COUNT_USERS_ONCE_TYPE.LAST;
  }

  if (nextCountUsersOnceType === DefaultCountUsersOnceType.get(metricType)) {
    nextCountUsersOnceType = null;
  }

  if (
    metricType === MetricType.Funnel &&
    aggregationOperator !== FUNNELS_MATH_TYPES.UNIQUE &&
    aggregationOperator !== FUNNELS_MATH_TYPES.CONVERSION_RATE_UNIQUE
  ) {
    // segmentMethod is only applicable to unique user counts in funnels
    nextCountUsersOnceType = null;
  }

  return nextCountUsersOnceType;
}

/**
 * Traverses to the bottom most layer of the iterator and returns true if found an aggregation that meets the given condition
 * this is used to perform a pattern matching against the operator and perUserAggregator
 * Boolean(perUserAggregator) -> INSIGHTS_MENU_ITEMS.PER_USER_AGGREGATION
 * operator === TOTAL -> INSIGHTS_MENU_ITEMS.TOTAL_AGGREGATION
 * operator === UNIQUE -> INSIGHTS_MENU_ITEMS.UNIQUE_AGGREGATION
 */
function hasMatchingAggregation(
  aggregation: AggregationSelector,
  callback: (param: {operator: MATH_TYPE; perUserOperator: PER_USER_AGGREGATIONS}) => boolean,
) {
  const {operator, perUserOperator} = aggregation;
  const aggregations = aggregation.subAggregations || aggregation.subSections;
  if (!aggregations) {
    return callback({operator, perUserOperator});
  }
  for (const aggregationItem of aggregations) {
    if (hasMatchingAggregation(aggregationItem, callback)) {
      return true;
    }
  }
  return false;
}

type CountMenuMeasurement = {
  aggregationOperator: MATH_TYPE;
  aggregationOperatorPerUser: PER_USER_AGGREGATIONS;
  percentile?: number;
  property: PropertySectionItem;
  rolling: {rollingWindowSize?: number} | null;
  cumulative: boolean;
  countUsersOnceType: COUNT_USERS_ONCE_TYPE;
};

function getMeasurementFromEntry(entry: Entry): CountMenuMeasurement {
  let aggregationOperator: MATH_TYPE;
  let aggregationOperatorPerUser: PER_USER_AGGREGATIONS;
  let percentile: number | undefined = undefined;
  let property: PropertySectionItem;
  let rolling: {rollingWindowSize?: number} = null;
  let cumulative = false;
  let countUsersOnceType = null;

  if (isMetricEntry(entry)) {
    aggregationOperator = entry.measurement.aggregationOperator;
    aggregationOperatorPerUser = entry.measurement.aggregationOperatorPerUser || null;
    property = entry.measurement.property;
    rolling = entry.measurement.rolling;
    cumulative = entry.measurement.cumulative;
    countUsersOnceType = entry.measurement.countUsersOnceType;
    percentile = entry.measurement.percentile;
  } else {
    aggregationOperator = entry.aggregationOperator;
    percentile = entry.percentile;
    aggregationOperatorPerUser = entry.aggregationOperatorPerUser || null;
    property = entry.property;
    rolling = entry.rolling;
    cumulative = entry.cumulative;
    countUsersOnceType = entry.countUsersOnceType;
  }
  return {
    aggregationOperator,
    aggregationOperatorPerUser,
    percentile,
    property,
    rolling,
    cumulative,
    countUsersOnceType,
  };
}

export function convertMetricToCountMenuMeasurement({
  measurement,
  behavior,
}: {
  measurement: BehaviorMeasurement;
  behavior: Behavior;
}): CountMenuMeasurement {
  return {
    aggregationOperator: measurement.math,
    aggregationOperatorPerUser: measurement.perUserAggregation || null,
    property: convertBookmarkPropertyToPropertySelection(measurement.property, behavior),
    rolling: measurement.rolling,
    cumulative: measurement.cumulative,
    countUsersOnceType: measurement.segmentMethod || null,
  };
}

function getCountMenuItems(metricType: BookmarkTypes.MetricType) {
  switch (metricType) {
    case MetricType.Funnel:
      return FUNNELS_COUNT_MENU_ITEMS(false);
    case MetricType.Retention:
    case MetricType.RetentionFrequency:
      return RETENTION_COUNT_MENU_ITEMS(false, true);
    default:
      return INSIGHTS_COUNT_MENU_ITEMS;
  }
}

export function getCountTypeForMeasurement({
  measurement,
  metricType,
}: {
  measurement: CountMenuMeasurement;
  metricType: BookmarkTypes.MetricType;
}): CountType {
  const countMenuItems = getCountMenuItems(metricType);

  const condition = ({operator, perUserOperator}) => {
    return (
      (operator ?? null) === (measurement.aggregationOperator ?? null) &&
      (perUserOperator ?? null) === (measurement.aggregationOperatorPerUser ?? null)
    );
  };

  for (const countMenuItem of countMenuItems) {
    const {countType, forProperty, operator, perUserOperator, aggregations} = countMenuItem;
    if (Boolean(forProperty) === Boolean(measurement.property)) {
      if (aggregations) {
        for (const aggregation of aggregations) {
          if (hasMatchingAggregation(aggregation, condition)) return countType;
        }
      } else {
        if (condition({operator, perUserOperator})) {
          return countType;
        }
      }
    }
  }
  return null;
}

export function getCountByForMetric(metric: BehaviorMetric): CountBy {
  const measurement = convertMetricToCountMenuMeasurement(metric.definition);
  const metricType = metric.definition.behavior.type;
  const countType = getCountTypeForMeasurement({
    metricType,
    measurement,
  });
  return getCountBy({measurement, countType});
}

export function getSelectedCountBy(entry: Entry): CountBy {
  const measurement = getMeasurementFromEntry(entry);
  const metricType = isMetricEntry(entry) ? entry.behavior.type : MetricType.Event;
  const countType = getCountTypeForMeasurement({
    metricType,
    measurement,
  });

  return getCountBy({measurement, countType});
}

function getCountBy({measurement, countType}: {measurement: CountMenuMeasurement; countType: CountType}): CountBy {
  const {
    aggregationOperator,
    aggregationOperatorPerUser,
    property,
    rolling,
    cumulative,
    countUsersOnceType,
    percentile,
  } = measurement;
  const advancedMenuValue: Calculation = {
    calculationType: null,
  };
  if (rolling) {
    advancedMenuValue.calculationType = CalculationTypes.Rolling;
    advancedMenuValue[`rollingWindowSize`] = rolling.rollingWindowSize;
  } else if (cumulative) {
    advancedMenuValue.calculationType = CalculationTypes.Cumulative;
  }

  const countBy: CountBy = {
    countType,
    aggregationOperator,
    aggregationOperatorPerUser,
    percentile,
    property,
    countUsersOnceType,
    advancedMenuValue,
  };

  return countBy;
}
